/**
 * frontend/src/features/experimental/ErrorBoundary.tsx — shell-level error containment (R0).
 *
 * The Experimental TAB boundary (over the hub + detail pages), distinct from each tool's own
 * boundary. Golden-Rule isolation: the shell knows nothing about any individual tool, so it
 * can't import a tool's boundary. Any render error here is contained — the core app shell and
 * every other tab keep working.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  failed: boolean;
}

export class ExperimentalErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Contained on purpose — never rethrow into the core error stream.
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          role="alert"
          data-testid="experimental-error-boundary"
          className="m-6 rounded-2xl border border-[var(--color-paper-60)] bg-[var(--color-paper)] p-6 text-[var(--color-ink)]"
        >
          <p className="text-sm font-semibold">The Experimental tab hit an error.</p>
          <p className="mt-1 text-sm text-[var(--color-ink-40)]">
            It is fully contained — the rest of Beyond Sure is unaffected. Reload to retry.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
